/**
 * Page de listing des opérations (Bons de Commande + Marchés Subséquents)
 * 
 * Ce composant affiche une liste d'opérations avec filtrage par onglets,
 * recherche et pagination.
 */

import './Operation.css';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import { useAuth } from "../../contexts/AuthContext";
import { isClientRole } from '../../utils/roleUtils';
import { fetchUnreadCountsMap } from '../../services/messagerie/messagerieUnread';
import { useNotifications } from "../../contexts/NotificationsContextType";
import { handleExportCSV } from '../../utils/commonHandlers';
import { acheteurRowTabKind } from '../../utils/commandeRowTab';
import { libelleStatut, tronquerLibelleStatut } from '../../utils/statusLabel';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';
import SearchBar from '../../composants/SearchBar/SearchBar';
import TableSkeleton from '../../composants/Skeleton/TableSkeleton';
import Button from '../../composants/Button/Button';
import LibelleNouveauMarche from '../../composants/LibelleNouveauMarche/LibelleNouveauMarche';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import TabNav from '../../composants/TabNav/TabNav';
import FiltreDeCommande from '../../composants/FiltreDeCommande/FiltreDeCommande';
import LancerMarcheModal from '../../composants/LancerMarcheModal/LancerMarcheModal';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import { formatDate, formatPrice } from '../../utils/entityUtils';

// Types et configuration
import { Operation, ApiOperation, ApiStatus, FilterOptions, PaginationState } from './types';
import { TABS, TAB_TO_STATUS_MAPPING, TYPE_OPTIONS } from './statusConfig';
import {
  transformApiOperationsToDisplay,
  transformBciOperationToDisplay,
  getStatusActionForRole,
  getUserRoleFromStorage,
  toCssClass,
  sortPaniersImportesByPriority,
  formatDateFR
} from './utils';
import {
  transformFiltersToApiParams,
  buildQueryString,
  ApiOperationFilters
} from './api';

// ============================================================================
// Types Props du composant
// ============================================================================

interface OperationPageProps {
  onNotificationUpdate?: (count: number) => void;
}

// ============================================================================
// Composant Principal
// ============================================================================

function OperationPage({ onNotificationUpdate }: OperationPageProps) {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const navigate = useNavigate();
  const location = useLocation();
  const { setUnreadOperations } = useNotifications();
  const abortControllerRef = useRef<AbortController | null>(null);

  // ============================================================================
  // États
  // ============================================================================

  // Modaux — initialisation directe depuis location.state pour éviter le flash de scroll
  const _navState = location.state as { openMarcheModal?: boolean; openBdcModal?: boolean; initialValues?: { lotTechnique?: string; lotGeographique?: string } } | null;
  const [isLancerMarcheModalOpen, setIsLancerMarcheModalOpen] = useState(() => !!_navState?.openMarcheModal);
  const [lancerMarcheInitialValues, setLancerMarcheInitialValues] = useState<{ lotTechnique?: string; lotGeographique?: string } | undefined>(() => _navState?.openMarcheModal ? _navState.initialValues : undefined);
  const [isBonCommandeModalOpen, setIsBonCommandeModalOpen] = useState(() => !!_navState?.openBdcModal);
  const [bonCommandeInitialValues, setBonCommandeInitialValues] = useState<{ lotTechnique?: string; lotGeographique?: string } | undefined>(() => _navState?.openBdcModal ? _navState.initialValues : undefined);

  // Nettoyer le state de navigation pour éviter la ré-ouverture si rechargement
  useEffect(() => {
    if (_navState?.openMarcheModal || _navState?.openBdcModal) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [operationToRefuse, setOperationToRefuse] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState<string | null>(null);
  const [operationToDeleteSource, setOperationToDeleteSource] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Données
  const [operations, setOperations] = useState<Operation[]>([]);
  const [filteredData, setFilteredData] = useState<Operation[]>([]);
  const [dataArchive, setDataArchive] = useState<any[]>([]);

  // Navigation et filtres
  const [activeTab, setActiveTab] = useState(() => TABS.indexOf('Tous'));
  const [selectedType, setSelectedType] = useState<string>(TYPE_OPTIONS[0].value);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<Partial<FilterOptions>>({});
  // Terme de la barre de recherche. C'est un ETAT, pas un fetch a part : il doit se
  // combiner a l'onglet actif, au filtre de type et a la pagination. L'ancien
  // handleSearch appelait l'API en direct et perdait ces trois dimensions.
  const [searchQuery, setSearchQuery] = useState<string>('');

  // États de chargement
  const [loading, setLoading] = useState<boolean>(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compteurs
  const [actionRequiseCount, setActionRequiseCount] = useState<number>(0);
  const [paniersImportesCount, setPaniersImportesCount] = useState<number>(0);
  const [unreadLocalOperations, setUnreadLocalOperations] = useState<number>(0);
  // Pastilles messagerie, keyees par `${source}:${operationId}` (MS et BDC vivent dans deux bases :
  // leurs ids d'operation peuvent se chevaucher, la cle composite evite la collision).
  const [unreadMsgCounts, setUnreadMsgCounts] = useState<Record<string, { questions_reponses: number; messagerie: number }>>({});

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    lastPage: 1,
    perPage: 50,
    total: 0
  });

  const itemsPerPage = 50;

  // ============================================================================
  // Fonctions de navigation
  // ============================================================================

  /**
   * Génère l'URL de base pour une opération selon son type et l'onglet actif
   */
  const getOperationBaseUrl = useCallback((operation: Operation): string => {
    const isPaniersImportes = TABS[activeTab] === 'Paniers importés';

    if (isPaniersImportes || operation.source === 'BCI') {
      return `/operation-bci/${operation.id}`;
    }
    return operation.source === 'BC'
      ? `/operation-bc/${operation.id}`
      : `/operation-ms/${operation.id}`;
  }, [activeTab]);

  const handleViewDetails = useCallback((id: string) => {
    const operation = operations.find(op => op.id === id);
    if (!operation) return;
    navigate(getOperationBaseUrl(operation));
  }, [operations, getOperationBaseUrl, navigate]);

  const handleCandidater = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=1`, { state: { action: 'candidater' } });
  }, [operations, getOperationBaseUrl, navigate]);

  // BC "A relancer" : onglet Consultation du rang a relancer.
  // Onglets BC acheteur entrelaces : Historique(0), Consultation R1(1), Offre R1(2),
  // Consultation R2(3), Offre R2(4), Consultation R3(5)... => index = 1 + (rang - 1) * 2.
  // La liste /operations n'expose pas toujours le detail des rangs : on calcule si dispo,
  // sinon defaut tab=3 (Consultation Rang 2, cas de relance le plus frequent).
  const getBcRelanceTab = useCallback((operation: any): number => {
    const commandes = operation?.commandes || [];
    if (Array.isArray(commandes) && commandes.length > 0) {
      const sorted = commandes
        .map((c: any) => ({ rang: parseInt(c.rang), s: c.id_status }))
        .sort((a: any, b: any) => a.rang - b.rang);
      for (const c of sorted) {
        // rang en brouillon (1) dont le precedent est refuse (8) ou expire (9) => rang a relancer
        if (c.rang > 1 && c.s === 1) {
          const prev = sorted.find((p: any) => p.rang === c.rang - 1);
          if (prev && (prev.s === 8 || prev.s === 9)) {
            return 1 + (c.rang - 1) * 2;
          }
        }
      }
    }
    const rangActuel = parseInt(operation?.rang_actuel);
    if (rangActuel && rangActuel > 1) {
      return 1 + (rangActuel - 1) * 2;
    }
    return 3;
  }, []);

  const handleAnalyser = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;

    const isPaniersImportes = TABS[activeTab] === 'Paniers importés';
    let tab: number;
    if (isPaniersImportes || operation.source === 'BCI') {
      tab = 1;
    } else if (operation.source === 'BC') {
      // BC "A relancer" : Consultation du rang a relancer
      tab = getBcRelanceTab(operation);
    } else {
      // MS : onglet Analyse
      tab = 3;
    }

    navigate(`${getOperationBaseUrl(operation)}?tab=${tab}`, { state: { action: 'analyser' } });
  }, [operations, activeTab, getOperationBaseUrl, navigate, getBcRelanceTab]);

  const handleReprendre = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=1`, { state: { action: 'reprendre' } });
  }, [operations, getOperationBaseUrl, navigate]);

  const handleSigner = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    // MS acheteur : onglet "Notifications et signatures" = index 4
    // (decale de 3 a 4 depuis l'insertion de l'onglet "Questions et reponses" en index 2)
    navigate(`${getOperationBaseUrl(operation)}?tab=4`, { state: { action: 'signer' } });
  }, [operations, getOperationBaseUrl, navigate]);

  const handleAccepter = useCallback((operationId: string) => {
    const operation = operations.find(op => op.id === operationId);
    if (!operation) return;
    navigate(`${getOperationBaseUrl(operation)}?tab=2`, { state: { action: 'accepter' } });
  }, [operations, getOperationBaseUrl, navigate]);

  // ============================================================================
  // Actions
  // ============================================================================

  const handleRefuserClick = (id: string) => {
    setOperationToRefuse(id);
    setIsRefuseModalOpen(true);
  };

  const handleConfirmRefuse = async () => {
    if (!operationToRefuse) {
      setIsRefuseModalOpen(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/${operationToRefuse}/refuser`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors du refus de l\'opération');

      // Mise à jour locale de l'état
      const updateStatus = (op: Operation) => {
        if (op.id === operationToRefuse) {
          return {
            ...op,
            status: { ...op.status, nom: 'Refusé', action: false }
          };
        }
        return op;
      };

      setOperations(prev => prev.map(updateStatus));
      setFilteredData(prev => prev.map(updateStatus));
    } catch (error) {
      console.error('Erreur lors du refus:', error);
      alert('Erreur lors du refus de l\'opération.');
    }

    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  const handleCancelRefuse = () => {
    setIsRefuseModalOpen(false);
    setOperationToRefuse(null);
  };

  const handleRelancerSignataire = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/subsequents/operation-ms/${id}/relancer-signataire`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors de la relance');
      alert('Relance envoyée avec succès');
    } catch (error) {
      console.error('Erreur lors de la relance:', error);
      alert('Une erreur est survenue lors de l\'envoi de la relance');
    }
  };

  const handleExport = useCallback((id: string) => {
    const operationToExport = operations.find(op => op.id === id);
    if (operationToExport) {
      handleExportCSV(
        [operationToExport],
        columns,
        `Operation-${id}-${new Date().toISOString().slice(0, 10)}.csv`
      );
    }
  }, [operations]);

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operations/exportOperations`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) throw new Error('Erreur lors de l\'exportation');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-operations-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Une erreur est survenue lors de l\'export des opérations');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = async (operation: Operation) => {
    try {
      const externalId = operation.source === 'BCI'
        ? `${operation.departement}${operation.numero || operation.id}F`
        : operation.numero || operation.id;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signed-contrat`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            external_id: externalId,
            external_type: 'operation',
            source: operation.source,
          })
        }
      );

      if (!response.ok) throw new Error('Erreur lors du téléchargement');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const annee = process.env.REACT_APP_ANNEE_EDITION || "25";
      a.download = `${annee}${operation.numero} (signed).pdf`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue lors du téléchargement du marché signé');
    }
  };

  const downloadArchivePDF = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive/${id}/download`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Erreur téléchargement archive');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archive-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Impossible de télécharger le fichier PDF.');
    }
  };

  /**
   * Ouvre la popup de confirmation pour supprimer un brouillon
   */
  const handleDeleteBrouillon = (id: string, source: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    setOperationToDelete(id);
    setOperationToDeleteSource(source);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  /**
   * Confirme la suppression du brouillon
   */
  const handleConfirmDelete = async () => {
    if (!operationToDelete || isDeleting) {
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // URL différente selon le type d'opération
      const deleteUrl = operationToDeleteSource === 'MS'
        ? `${process.env.REACT_APP_API_URL}/subsequents/operation/${operationToDelete}/delete`
        : `${process.env.REACT_APP_API_URL}/bdc/operation/${operationToDelete}/delete`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la suppression du brouillon');
      }

      // Retirer l'opération de la liste locale
      setOperations(prev => prev.filter(op => op.id !== operationToDelete));
      setFilteredData(prev => prev.filter(op => op.id !== operationToDelete));

      // Mettre à jour le total de la pagination
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));

      // Fermer la popup
      setIsDeleteModalOpen(false);
      setOperationToDelete(null);
      setOperationToDeleteSource(null);
      setIsDeleting(false);
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setDeleteError(error.message || 'Une erreur est survenue lors de la suppression du brouillon.');
      setIsDeleting(false);
    }
  };

  /**
   * Annule la suppression du brouillon
   */
  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setOperationToDelete(null);
    setOperationToDeleteSource(null);
    setDeleteError(null);
  };

  // ============================================================================
  // Handlers de navigation et filtres
  // ============================================================================

  const handleTabChange = (tabIndex: number) => {
    if (tabIndex === activeTab) return;

    setLoading(true);
    setError(null);
    setOperations([]);
    setFilteredData([]);
    setDataArchive([]);
    setActiveTab(tabIndex);
    setCurrentPage(1);
    // Réinitialiser les filtres lors du changement d'onglet
    setActiveFilters({});
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    fetchOperations(1, activeTab, value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    if (TABS[activeTab] === 'Archive') {
      fetchArchives({ page });
    } else if (TABS[activeTab] === 'Paniers importés') {
      fetchPaniersImportes(page);
    } else {
      fetchOperations(page, activeTab);
    }
  };

  const handleRowClick = (id: string) => {
    const operation = operations.find(op => op.id === id);
    if (!operation) return;

    const baseUrl = getOperationBaseUrl(operation);
    const userRole = getUserRoleFromStorage();
    const statusId = operation.status?.id || 0;

    if (!statusId) {
      navigate(`${baseUrl}?tab=0`, { state: { action: '' } });
      return;
    }

    const actionMapping = getStatusActionForRole(statusId, userRole, operation.source);
    const statusName = operation.status.nom;
    const source = operation.source;
    // BC "a relancer" (rang precedent refuse/expire) : cas SPECIAL au rang, garde par getBcRelanceTab
    // qui vise la Consultation du rang exact -> reste en index, la regle par kind ne sait pas viser un rang.
    if (source === 'BC' && (actionMapping.action === 'analyser' || statusName === 'À analyser')) {
      navigate(`${baseUrl}?tab=${getBcRelanceTab(operation)}`, { state: { action: actionMapping.action } });
      return;
    }

    // BCI : comportement historique conserve (le routing par kind ne couvre que BC/MS).
    if (source === 'BCI') {
      const tabBci = statusName === 'À accepter' ? 1 : 0;
      navigate(`${baseUrl}?tab=${tabBci}`, { state: { action: actionMapping.action } });
      return;
    }

    // BC/MS : destination par KIND d'onglet (resolu par useOperationTabs / useOperationMsTabs), et non
    // par index en dur qui se trompait de cible — ex. une operation "Validee" atterrissait sur Offre au
    // lieu de Notification. La regle metier vit dans commandeRowTab. Fallback historique si inconnu.
    const kind = acheteurRowTabKind(source, statusId) ?? 'historique';
    navigate(`${baseUrl}?tab=${kind}`, { state: { action: actionMapping.action } });
  };

  const handleFilter = () => setIsFilterModalOpen(true);

  const handleSelectionChange = (selectedIds: string[]) => setSelectedItems(selectedIds);

  // ============================================================================
  // Recherche
  // ============================================================================

  /**
   * Barre de recherche.
   *
   * Elle ne déclenche plus d'appel API à part : elle pose le terme dans l'état et
   * laisse l'effet principal recharger l'onglet courant. L'ancienne version tapait
   * `/operation?operation_nom=...` en direct, ce qui avait trois défauts : elle
   * ignorait les filtres de statut de l'onglet et le filtre de type actifs, elle
   * cassait la pagination (page figée à 1), et elle ne cherchait que dans le libellé
   * — une opération n'était donc pas trouvable par son numéro ni sa référence. Le
   * paramètre `search` couvre les trois colonnes.
   */
  const handleSearch = (query: string) => {
    const trimmed = query.trim();

    if (trimmed === searchQuery) {
      // Même terme resoumis : rien à recharger, on ne laisse pas tourner le spinner.
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setCurrentPage(1);
    setSearchQuery(trimmed);
  };

  // ============================================================================
  // Filtres
  // ============================================================================

  const maxPrice = useMemo(
    () => Math.max(...operations.map(op => op.prixTotal || 0), 0),
    [operations]
  );

  /**
   * Vérifie si des filtres sont actuellement actifs
   * Prend en compte TOUS les filtres possibles
   */
  const hasActiveFilters = useMemo(() => {
    // Vérification de chaque filtre individuellement pour plus de clarté
    const hasLotTechnique = !!activeFilters.lotTechnique;
    const hasLotGeographique = !!activeFilters.lotGeographique;
    const hasNomOperation = !!activeFilters.nomOperation;
    const hasEntite = !!activeFilters.entite;
    const hasCreateur = !!activeFilters.createur;
    const hasStatus = !!activeFilters.status;
    const hasType = !!(activeFilters.type && activeFilters.type !== 'Tous' && activeFilters.type !== '');
    const hasPrixMin = !!(activeFilters.prixRange && activeFilters.prixRange.min > 0);
    const hasPrixMax = !!(activeFilters.prixRange && activeFilters.prixRange.max > 0);
    const hasDateStart = !!(activeFilters.dateRange && activeFilters.dateRange.start);
    const hasDateEnd = !!(activeFilters.dateRange && activeFilters.dateRange.end);

    return (
      hasLotTechnique ||
      hasLotGeographique ||
      hasNomOperation ||
      hasEntite ||
      hasCreateur ||
      hasStatus ||
      hasType ||
      hasPrixMin ||
      hasPrixMax ||
      hasDateStart ||
      hasDateEnd
    );
  }, [activeFilters]);

  /**
   * Applique les filtres en appelant l'API avec les paramètres transformés
   * Les filtres côté serveur utilisent les QueryFilters (OperationFilter.php)
   */
  const handleFilterApply = async (filters: FilterOptions) => {
    // Fermer le modal immédiatement
    setIsFilterModalOpen(false);

    // Sauvegarder les filtres actifs
    setActiveFilters(filters);

    // Réinitialiser la page à 1 lors de l'application de nouveaux filtres
    setCurrentPage(1);

    // Charger les données avec les nouveaux filtres selon l'onglet actif
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchPaniersImportesWithFilters(1, filters);
    } else {
      await fetchOperationsWithFilters(1, activeTab, selectedType, filters);
    }
  };

  /**
   * Réinitialise tous les filtres et recharge les données
   */
  const handleClearFilters = async () => {
    setActiveFilters({});
    setCurrentPage(1);
    // IMPORTANT: Passer explicitement {} car setActiveFilters est asynchrone
    // et activeFilters n'est pas encore mis à jour à ce stade
    if (TABS[activeTab] === 'Paniers importés') {
      await fetchPaniersImportesWithFilters(1, {});
    } else {
      await fetchOperationsWithFilters(1, activeTab, selectedType, {});
    }
  };

  // ============================================================================
  // Fonctions de récupération des données
  // ============================================================================

  /**
   * Récupère les opérations avec support des filtres
   * @param page - Page courante
   * @param tab - Onglet actif (détermine les filtres de statut)
   * @param type - Type d'opération (BC/MS/Tous)
   * @param filters - Filtres optionnels de l'interface utilisateur
   */
  const fetchOperationsWithFilters = async (
    page = 1,
    tab = activeTab,
    type = selectedType,
    filters: Partial<FilterOptions> = {}
  ) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    setOperations([]);
    setFilteredData([]);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // Construire les paramètres API à partir des filtres UI
      const apiFilters = transformFiltersToApiParams(filters, page, 100);

      // La recherche se cumule aux filtres de la popup et au filtre d'onglet (ET),
      // au lieu de les remplacer comme le faisait l'appel direct précédent.
      if (searchQuery) {
        apiFilters.search = searchQuery;
      }

      // Ajouter les filtres par onglet (statut)
      if (tab !== 0 && tab < TABS.length) {
        const tabMapping = TAB_TO_STATUS_MAPPING[tab];
        if (tabMapping?.filter) {
          // Parser les filtres de l'onglet et les ajouter
          const tabFilters = tabMapping.filter.split('&&');
          tabFilters.forEach(f => {
            const [key, value] = f.split('=');
            if (key && value) {
              (apiFilters as any)[key] = value;
            }
          });
        }
      }

      // Filtre par type (BC/MS)
      if (type !== 'Tous') {
        apiFilters.type = type;
      }

      // Construire l'URL avec les paramètres
      const queryString = buildQueryString(apiFilters);

      console.log('Filtres API envoyés:', apiFilters);
      console.log('Query string:', queryString);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operation?${queryString}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;
      if (!response.ok) throw new Error(`Erreur: ${response.statusText}`);

      const responseData = await response.json();

      if (responseData.status !== 'success' || !Array.isArray(responseData.data)) {
        throw new Error('Format de réponse API inattendu');
      }

      // Pagination
      if (responseData.pagination) {
        setPagination({
          currentPage: responseData.pagination.current_page || 1,
          lastPage: responseData.pagination.last_page || 1,
          perPage: responseData.pagination.per_page || 50,
          total: responseData.pagination.total || 0
        });
      }

      // Transformation des données
      const formattedData = transformApiOperationsToDisplay(responseData.data);

      // Filtrer les brouillons de l'onglet "En attente"
      let finalData = tab === 2
        ? formattedData.filter(op => op.status.id !== 1)
        : formattedData;

      // Appliquer les filtres locaux pour les champs non supportés par l'API
      // (ex: dateRange, createur qui nécessitent des jointures complexes)
      if (filters.dateRange?.start && filters.dateRange?.end) {
        finalData = finalData.filter(op => {
          const opDate = new Date(op.dateCreation.split('/').reverse().join('-'));
          return opDate >= filters.dateRange!.start! && opDate <= filters.dateRange!.end!;
        });
      }

      if (filters.createur) {
        finalData = finalData.filter(op =>
          op.creePar.toLowerCase().includes(filters.createur!.toLowerCase())
        );
      }

      // Filtre sur entite (organisme pour prestataire, prestataire pour acheteur)
      if (filters.entite) {
        finalData = finalData.filter(op => {
          // Pour les acheteurs: filtrer sur prestataireNom
          // Pour les prestataires: filtrer sur organismeNom
          const nomAComparer = isClientView ? op.prestataireNom : op.organismeNom;
          return nomAComparer === filters.entite;
        });
      }

      setFilteredData(finalData);
      setOperations(finalData);
      updateNotifications(formattedData);

      // Charger les badges messagerie (MS + BDC) en batch, keyes par `${source}:${operationId}`.
      const auteurType = isClientView ? 'acheteur' : 'prestataire';
      const msOps = finalData.filter(op => op.source === 'MS');
      const bcOps = finalData.filter(op => op.source === 'BC');
      // Q&R MS prestataire : timestamps de derniere lecture (localStorage). Sans objet en BDC (pas de Q&R).
      const msSince = !isClientView
        ? msOps.map(op => {
          const lastRead = localStorage.getItem(`qr_last_read_${op.id}`);
          return lastRead ? `since[${op.id}]=${encodeURIComponent(lastRead)}` : null;
        }).filter(Boolean).join('&')
        : '';
      try {
        const [msMap, bcMap] = await Promise.all([
          fetchUnreadCountsMap('/subsequents', auteurType, msOps.map(op => op.id), msSince),
          fetchUnreadCountsMap('/bdc', auteurType, bcOps.map(op => op.id)),
        ]);
        const merged: Record<string, { questions_reponses: number; messagerie: number }> = {};
        Object.entries(msMap).forEach(([id, c]) => { merged[`MS:${id}`] = c; });
        Object.entries(bcMap).forEach(([id, c]) => { merged[`BC:${id}`] = c; });
        setUnreadMsgCounts(merged);
      } catch {
        // Silencieux : les badges ne sont pas critiques
      }

    } catch (err: any) {
      if (err.name === "AbortError") return;
      console.error("Erreur de chargement:", err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    }
  };

  /**
   * Fonction simplifiée pour charger les opérations sans filtres personnalisés
   */
  const fetchOperations = async (page = 1, tab = activeTab, type = selectedType) => {
    // Utiliser les filtres actifs s'ils existent
    await fetchOperationsWithFilters(page, tab, type, activeFilters);
  };

  const fetchArchives = async (params: Record<string, any> = {}) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    try {
      // Les archives sont servies par un contrôleur distinct (bdc-archive), dont le seul
      // filtre texte est le numéro de marché. La barre de recherche y reste donc active,
      // mais avec cette portée-là.
      const queryParams = {
        ...params,
        per_page: '100',
        ...(searchQuery && { numero_marche: searchQuery }),
      };
      const query = new URLSearchParams(queryParams).toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc-archive?${query}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        setDataArchive(result.data || []);
        setPagination({
          currentPage: result.pagination.current_page,
          lastPage: result.pagination.last_page,
          perPage: result.pagination.per_page,
          total: result.pagination.total
        });
      } else {
        setDataArchive([]);
      }
    } catch (error: any) {
      if (error.name === "AbortError") return;
      console.error('Erreur réseau Archives:', error);
      setDataArchive([]);
    }
  };

  const fetchPaniersImportes = async (page = 1) => {
    // Utiliser les filtres actifs s'ils existent
    await fetchPaniersImportesWithFilters(page, activeFilters);
  };

  /**
   * Récupère les paniers importés (BDCI) avec support des filtres
   */
  const fetchPaniersImportesWithFilters = async (
    page = 1,
    filters: Partial<FilterOptions> = {}
  ) => {
    // Utiliser le contrôleur existant s'il n'est pas annulé, sinon en créer un nouveau
    let controller = abortControllerRef.current;
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      abortControllerRef.current = controller;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Aucun token d\'authentification trouvé');

      // Construire les paramètres de requête
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('per_page', '100');

      // La recherche se cumule aux filtres de la popup (ET).
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      // Ajouter les filtres API
      if (filters.nomOperation) {
        params.append('operation_nom', filters.nomOperation);
      }
      if (filters.lotTechnique) {
        // Extraire uniquement le code du lot technique (ex: "202" de "202 - Gros œuvre")
        const lotCode = filters.lotTechnique.split(' - ')[0].trim();
        params.append('operation_lot_technique', lotCode);
      }
      if (filters.lotGeographique) {
        params.append('operation_departement', filters.lotGeographique);
      }
      if (filters.entite) {
        params.append('operation_client', filters.entite);
      }
      if (filters.status) {
        params.append('operation_status', filters.status);
      }
      if (filters.prixRange?.min && filters.prixRange.min > 0) {
        params.append('operation_prix_total_min', filters.prixRange.min.toString());
      }
      if (filters.prixRange?.max && filters.prixRange.max > 0) {
        params.append('operation_prix_total_max', filters.prixRange.max.toString());
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdci/operations?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: controller.signal,
        }
      );

      if (controller.signal.aborted) return;

      const result = await response.json();

      if (result.status === 'success') {
        const formattedData = result.data.map(transformBciOperationToDisplay);
        let finalData = sortPaniersImportesByPriority(formattedData);

        // Appliquer les filtres locaux pour les champs non supportés par l'API
        if (filters.dateRange?.start && filters.dateRange?.end) {
          finalData = finalData.filter((op: Operation) => {
            const opDate = new Date(op.dateCreation.split('/').reverse().join('-'));
            return opDate >= filters.dateRange!.start! && opDate <= filters.dateRange!.end!;
          });
        }

        if (filters.createur) {
          finalData = finalData.filter((op: Operation) =>
            op.creePar.toLowerCase().includes(filters.createur!.toLowerCase())
          );
        }

        // Filtre sur entite (organisme pour prestataire, prestataire pour acheteur)
        if (filters.entite) {
          finalData = finalData.filter((op: Operation) => {
            const nomAComparer = isClientView ? op.prestataireNom : op.organismeNom;
            return nomAComparer === filters.entite;
          });
        }

        setOperations(finalData);
        setFilteredData(finalData);

        // Compteur des paniers à accepter
        const countAAccepter = result.data.filter(
          (op: any) => op.status?.id === 4 && op.status?.action === true
        ).length;
        setPaniersImportesCount(countAAccepter);

        setPagination({
          currentPage: result.pagination?.current_page || page,
          lastPage: result.pagination?.last_page || 1,
          perPage: result.pagination?.per_page || 50,
          total: result.pagination?.total || finalData.length
        });
      } else {
        setOperations([]);
        setFilteredData([]);
        setPaniersImportesCount(0);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error('Erreur réseau Paniers importés:', error);
      setOperations([]);
      setFilteredData([]);
      setPaniersImportesCount(0);
    }
  };

  const fetchActionRequiseCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/operations-compteur?status_action=1`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        const count = result.data.count ?? 0;
        const countBDCI = result.data.count_bdci ?? 0;
        const totalCount = count + countBDCI;

        setActionRequiseCount(count);
        // Le compteur serveur agrege porte aussi count_bdci : on alimente la pastille
        // "Paniers importes" ici plutot que via un 2e appel /operations-compteur identique
        // (dedup perf : -1 requete gateway = -3 appels downstream par navigation).
        setPaniersImportesCount(countBDCI);
        setUnreadOperations(totalCount);
        setUnreadLocalOperations(totalCount);
        onNotificationUpdate?.(totalCount);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du compteur:', err);
    }
  };

  const updateNotifications = (operations: Operation[]) => {
    try {
      const count = operations.filter(op => op.actionRequise).length;
      setUnreadLocalOperations(count);
      // NOTE : on ne touche PLUS la pastille sidebar (setUnreadOperations) ici. Cette liste se charge
      // source par source (BC -> MS -> paniers importes) et faisait clignoter la pastille (baisse/remonte).
      // La pastille est alimentee une seule fois par le compteur serveur agrege (fetchActionRequiseCount).
      onNotificationUpdate?.(count);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notifications:", error);
    }
  };

  // ============================================================================
  // Notifications pour TabNav
  // ============================================================================

  const notifications = useMemo(() => [
    { tabIndex: TABS.indexOf('Tous'), count: actionRequiseCount },
    { tabIndex: TABS.indexOf('Action requise'), count: actionRequiseCount },
    { tabIndex: TABS.indexOf('Paniers importés'), count: paniersImportesCount }
  ].filter(n => n.tabIndex !== -1 && n.count > 0), [actionRequiseCount, paniersImportesCount]);

  // ============================================================================
  // Colonnes du tableau
  // ============================================================================

  // Colonnes pour les archives
  const archiveColumns = useMemo(() => [
    {
      field: 'type',
      header: 'Type',
      renderCell: () => <div className="type-info">BC</div>
    },
    {
      field: 'date_commande',
      header: 'Date',
      renderCell: (value: any) => <div>{formatDate(value, false)}</div>
    },
    {
      field: 'panier',
      header: 'Nom',
      renderCell: (value: string, row: any) => <div>{row.panier || '-'}</div>
    },
    {
      field: 'entite',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: any, row: any) => <div>{row.entite?.nom || '-'}</div>
    },
    {
      field: 'total_ht',
      header: 'Total HT',
      renderCell: (value: number, row: any) => <div>{formatPrice(Number(row?.total_ht))}</div>
    },
    {
      field: 'statut',
      header: 'Statut',
      renderCell: (value: string) => {
        const libelle = value.charAt(0).toUpperCase() + value.slice(1);
        // Tronque a 3 mots pour que la colonne cesse de s'elargir ; `title` restitue l'integralite.
        return (
          <div className={`status-badge ${toCssClass(value)}`} title={libelle}>
            {tronquerLibelleStatut(libelle)}
          </div>
        );
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: any, row: any) => (
        <Button
          value="PDF"
          variant="secondary"
          type="button"
          onClick={() => downloadArchivePDF(row.id)}
          className="action-button"
        />
      )
    }
  ], []);

  // Fonction de rendu de statut réutilisable
  const renderStatus = useCallback((value: ApiStatus, row: Operation) => {
    if (!value) return null;

    let statusClass = toCssClass(value.nom);
    const actionClass = value.action ? 'action-requise' : '';

    // Libelle affiche : table partagee (utils/statusLabel), commune au SuiviHeader et a la liste
    // prestataire — le meme dossier ne peut plus changer de nom d'un ecran a l'autre.
    const displayNom = libelleStatut(row.source, value.id, value.nom);

    // Les CLASSES restent locales : ce sont deux BC dont la pastille change de couleur, pas de
    // vocabulaire. Le libelle correspondant vient de la table ci-dessus.
    if (row.source === 'BC') {
      if (value.id === 1) statusClass = 'brouillon';
      if (value.id === 12) statusClass = 'a-relancer'; // "A relancer" : pastille orange
    }

    return (
      <div
        className={`status-badge ${statusClass} ${actionClass}`}
        // Le libelle affiche est tronque a 3 mots : le survol doit restituer le STATUT entier. Le
        // `title` portait « Type: {type_compte} » (acheteur / prestataire), une precision technique
        // qui aurait masque justement l'information coupee.
        title={displayNom}
      >
        {tronquerLibelleStatut(displayNom)}
      </div>
    );
  }, []);

  // Fonction de rendu des actions réutilisable
  const renderActions = useCallback((row: Operation) => {
    if (!row) return null;

    const statusId = row.status?.id || 0;
    const isActionRequired = row.actionRequise;
    const userRole = getUserRoleFromStorage();
    const actionMapping = getStatusActionForRole(statusId, userRole, row.source);

    const statusClass = toCssClass(row.status?.nom || '');

    const handleActionClick = () => {
      switch (actionMapping.action) {
        case 'reprendre': handleReprendre(row.id); break;
        case 'analyser': handleAnalyser(row.id); break;
        case 'candidater': handleCandidater(row.id); break;
        case 'signer': handleSigner(row.id); break;
        case 'export': handleExport(row.id); break;
        case 'download': handleDownload(row); break;
        case 'relancer': handleRelancerSignataire(row.id); break;
        case 'accepter': handleAccepter(row.id); break;
        default: console.log(`Aucune action définie pour le statut ${statusId}`);
      }
    };

    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {actionMapping.showButton && (
          <Button
            value={actionMapping.buttonText}
            variant="secondary"
            size="small"
            onClick={handleActionClick}
            className={`action-button ${statusClass} ${isActionRequired ? 'action-requise' : ''} ${actionMapping.action === 'download' ? 'action-button--pdf' : ''}`}
            disabled={actionMapping.disableButton}
          />
        )}
        {statusId === 1 && (
          <Button
            variant="cancel"
            value=""
            size="small"
            className="delete-member-button"
            icon={<DeleteIcon />}
            title="Supprimer le brouillon"
            onClick={(e) => handleDeleteBrouillon(row.id, row.source, e!)}
          />
        )}
      </div>
    );
  }, [handleReprendre, handleAnalyser, handleCandidater, handleSigner, handleExport, handleAccepter, handleDeleteBrouillon]);

  // Colonnes pour les paniers importés
  const paniersImportesColumns = useMemo(() => [
    {
      field: 'numero',
      header: 'N°',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'dateCreation',
      header: 'Date',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'lotTechniqueDisplay',
      header: 'Lot Technique',
      renderCell: (value: string) => (
        <div className="lot-technique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'departement',
      header: 'Dép',
      renderCell: (value: string) => (
        <div className="lot-geographique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="operation-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'prixTotal',
      header: 'Total HT',
      renderCell: (value: number) => (
        <div className="montant-operation">
          {value != null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: renderStatus
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: string, row: Operation) => renderActions(row)
    }
  ], [renderStatus, renderActions]);

  // Colonnes principales pour les opérations
  const columns = useMemo(() => [
    {
      field: 'id',
      header: '',
      width: '48px',
      renderCell: (_value: any, row: Operation) => {
        const counts = unreadMsgCounts[`${(row as any).source}:${row.id}`];
        const total = (counts?.questions_reponses ?? 0) + (counts?.messagerie ?? 0);
        if (total === 0) return null;
        return (
          <span className="msg-unread-badge" title={`${total} message(s) non lu(s)`}>
            {total}
          </span>
        );
      }
    },
    {
      field: 'numero',
      header: 'N°',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'source',
      header: 'Type',
      renderCell: (value: string) => (
        <div className="type-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'dateCreation',
      header: 'Date',
      renderCell: (value: string) => (
        <div className="date-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'lotTechniqueDisplay',
      header: 'Lot Technique',
      renderCell: (value: string) => (
        <div className="lot-technique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'departement',
      header: 'Dép',
      renderCell: (value: string) => (
        <div className="lot-geographique text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'creePar',
      header: 'Créé par',
      renderCell: (value: string) => (
        <div className="user-info text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'nom',
      header: 'Nom',
      renderCell: (value: string) => (
        <div className="operation-nom text-truncate" title={value || '-'}>{value || '-'}</div>
      )
    },
    {
      field: 'prestataireNom',
      header: isClientView ? 'Prestataire' : 'Client',
      renderCell: (value: string, row: Operation) => {
        let displayValue: string;
        if (isClientView) {
          // Acheteur. Priorité : retenu (prestataireNom) > entreprise consultée au rang courant
          // (BC en consultation, avant retenu) > prestataire ayant refusé (BC refusé) > '-'.
          // BCI affiche le nom du panier.
          if (row.source === 'BCI') {
            displayValue = row.nom;
          } else if (row.prestataireNom && row.prestataireNom !== 'Non défini') {
            // BC en attente d'acceptation d'un rang : le retenu provisoire est le candidat de ce
            // rang (id_commande déjà posé à l'acceptation). On précise le rang, déterminé par le
            // statut BC (3/5/6 = AttenteAcceptationRang 1/2/3). Validé (7) ou MS : nom seul.
            const rangParStatut: Record<number, number> = { 3: 1, 5: 2, 6: 3 };
            const rangAcceptation = row.source === 'BC' ? rangParStatut[row.status?.id ?? 0] : undefined;
            displayValue = rangAcceptation
              ? `${row.prestataireNom} (rang ${rangAcceptation})`
              : row.prestataireNom;
          } else if (row.entiteRangCourant?.nom) {
            displayValue = row.entiteRangCourant.rang != null
              ? `${row.entiteRangCourant.nom} (rang ${row.entiteRangCourant.rang})`
              : row.entiteRangCourant.nom;
          } else if (row.entiteRefus?.nom) {
            displayValue = row.entiteRefus.nom;
          } else {
            displayValue = '-';
          }
        } else {
          // Prestataire: afficher organismeNom ou 'Client non défini'
          displayValue = row.organismeNom && row.organismeNom !== 'Non défini' ? row.organismeNom : 'Client non défini';
        }
        return (
          <div className="client-info text-truncate" title={displayValue}>{displayValue}</div>
        );
      }
    },
    {
      field: 'prixTotal',
      header: 'Total HT',
      renderCell: (value: number) => (
        <div className="montant-operation">
          {value != null ? `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '0,00 €'}
        </div>
      )
    },
    {
      field: 'status',
      header: 'Statut',
      renderCell: renderStatus
    },
    {
      field: 'tempsRestant',
      header: 'Temps restant',
      renderCell: (value: string, row: Operation) => {
        if (!row || !row.expireAt) return null;
        // MS : le compte à rebours ne vaut QUE pendant la consultation (statut 2 = délai de remise
        // des offres). Au-delà (signé=11, etc.), expire_at est résiduel -> pas de chrono (sinon
        // « Expiré » à tort sur une opération vivante). Aligné sur SuiviHeader / operationStatus.
        // BC / autres : logique existante conservée (BC statut 2 « En attente de l'offre » + statut 9 « Expiré »).
        const show = row.source === 'MS'
          ? row.status?.id === 2
          : !((row.source === 'BC' && row.status.id === 2) || row.status.id === 9);
        return show ? <CompteARebourd targetDate={row.expireAt} /> : null;
      }
    },
    {
      field: 'actions',
      header: 'Action',
      renderCell: (value: string, row: Operation) => renderActions(row)
    }
  ], [isClientView, renderStatus, renderActions, unreadMsgCounts]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Mise à jour des notifications quand actionRequiseCount change.
  // La pastille sidebar (setUnreadOperations) N'est PAS pilotee ici : actionRequiseCount = `count`
  // SANS les paniers importes (count_bdci), donc plus bas que le total -> ca faisait baisser la pastille.
  // Le total (count + count_bdci) est pose une seule fois par fetchActionRequiseCount.
  useEffect(() => {
    setUnreadLocalOperations(actionRequiseCount);
    onNotificationUpdate?.(actionRequiseCount);
  }, [actionRequiseCount, onNotificationUpdate]);

  // Chargement des données au montage et changement d'onglet/type/page
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);

      try {
        if (TABS[activeTab] === 'Archive') {
          await fetchArchives({ page: currentPage, statut: TAB_TO_STATUS_MAPPING[activeTab].filter });
        } else if (TABS[activeTab] === 'Paniers importés') {
          await fetchPaniersImportes(currentPage);
        } else {
          await fetchOperations(currentPage, activeTab, selectedType);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          // La barre de recherche déclenche cet effet : c'est lui qui sait quand le
          // rechargement est réellement terminé, quel que soit l'onglet servi.
          setSearchLoading(false);
        }
      }
    };

    loadData();
    fetchActionRequiseCount();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, selectedType, currentPage, searchQuery]);

  // ============================================================================
  // Rendu
  // ============================================================================

  return (
    <div className="commandes-page">
      <section className="tableau-de-bord-section">
        <TabNav
          tabs={[...TABS]}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          notifications={notifications}
          rightAlignedTabs={['Archive', 'Paniers importés']}
        />

        <div className="button-group">
          <div className="button-filter-group">
            <SearchBar
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              placeholder="Trouver une commande"
              isLoading={searchLoading}
            />
            <Button
              value="exporter"
              variant="secondary"
              size="small"
              icon={<ExportIcon />}
              onClick={handleExportAll}
              disabled={isExporting}
            />
            <div className="filter-button-container">
              <Button
                value="filtrer"
                variant={hasActiveFilters ? "primary" : "secondary"}
                size="small"
                icon={<FilterIcon />}
                onClick={handleFilter}
              />
              {hasActiveFilters && (
                <button
                  className="filter-reset-button"
                  onClick={handleClearFilters}
                  title="Réinitialiser les filtres"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Actions de creation : alignees a droite de la ligne recherche/exporter/filtrer. */}
          <div className="commandes-actions">
            <Button
              value={<LibelleNouveauMarche forme="BC" />}
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsBonCommandeModalOpen(true)}
            />
            <Button
              value={<LibelleNouveauMarche forme="MS" />}
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsLancerMarcheModalOpen(true)}
            />
          </div>
        </div>

        {loading ? (
          <TableSkeleton
            rows={8}
            columns={
              TABS[activeTab] === 'Archive'
                ? archiveColumns
                : TABS[activeTab] === 'Paniers importés'
                  ? paniersImportesColumns
                  : columns
            }
          />
        ) : error ? (
          <div className="error-message">
            <p>Erreur: {error}</p>
          </div>
        ) : (
          <div className="tableau-operations">
            <TableauDeBord
              data={TABS[activeTab] === 'Archive' ? dataArchive : filteredData}
              columns={
                TABS[activeTab] === 'Archive'
                  ? archiveColumns
                  : TABS[activeTab] === 'Paniers importés'
                    ? paniersImportesColumns
                    : columns
              }
              onSearch={handleSearch}
              typeFilters={true}
              onExport={() => handleExportCSV(
                filteredData,
                columns as any,
                `Operations-${new Date().toISOString().slice(0, 10)}.csv`
              )}
              onRowClick={handleRowClick}
              isSelectable={true}
              showActionBar={true}
              selectedItems={selectedItems}
              onSelectionChange={handleSelectionChange}
              showActionsColumn={false}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              totalPages={pagination.lastPage}
              totalItems={pagination.total}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </section>

      {/* Modal Marché Subséquent */}
      <LancerMarcheModal
        isOpen={isLancerMarcheModalOpen}
        onClose={() => setIsLancerMarcheModalOpen(false)}
        title="Lancer un marché subséquent"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="MS"
        initialValues={lancerMarcheInitialValues}
      />

      {/* Modal Bon de Commande */}
      <LancerMarcheModal
        isOpen={isBonCommandeModalOpen}
        onClose={() => setIsBonCommandeModalOpen(false)}
        title="Nouveau Bon de Commande"
        showNomOperation={true}
        showLotTechnique={true}
        showLotGeographique={true}
        showDepartement={false}
        mode="BC"
        initialValues={bonCommandeInitialValues}
      />

      {/* Modal Filtres */}
      <FiltreDeCommande
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilterApply}
        data={operations}
        maxPrice={maxPrice}
        activeFilters={activeFilters}
      />

      {/* Modal Suppression Brouillon */}
      <ValidationPopUp
        isOpen={isDeleteModalOpen}
        title="Supprimer le brouillon"
        text={deleteError || "Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible."}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        cancelLabel="Annuler"
        confirmLabel="Supprimer"
        isLoading={isDeleting}
      />


    </div>
  );
}

export default OperationPage;
